import { useState, useEffect } from 'react';
import axios from 'axios';
import AdminSidebar from '@/components/AdminSidebar';
import { Plus, Pencil, Trash2, X, Check, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import ImageUpload from '@/components/ImageUpload';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const today = new Date().toISOString().split('T')[0];
const empty = { judul: '', konten: '', ringkasan: '', tanggal: today, foto: '' };

export default function AdminBerita() {
  const [berita, setBerita] = useState([]);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => { fetch(); }, []);
  const fetch = () => axios.get(`${API}/berita`).then(r => setBerita(r.data)).catch(() => {});

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editId) { await axios.put(`${API}/berita/${editId}`, form); toast.success('Berita diperbarui'); }
      else { await axios.post(`${API}/berita`, form); toast.success('Berita diterbitkan'); }
      setShowForm(false); setEditId(null); setForm(empty); fetch();
    } catch (err) { toast.error(err.response?.data?.detail || 'Gagal'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Hapus berita ini?')) return;
    await axios.delete(`${API}/berita/${id}`); toast.success('Berita dihapus'); fetch();
  };

  return (
    <div className="admin-layout">
      <AdminSidebar />
      <div className="admin-content bg-gray-50 min-h-screen">
        <div className="bg-white border-b border-gray-100 px-4 md:px-8 py-4 md:py-6 flex items-start md:items-center justify-between flex-wrap gap-2">
          <div>
            <h1 className="font-playfair text-2xl font-bold text-emerald-950">BQ News</h1>
            <p className="text-sm text-gray-500">{berita.length} artikel diterbitkan</p>
          </div>
          <button data-testid="add-berita-btn" onClick={() => { setForm(empty); setEditId(null); setShowForm(true); }}
            className="flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white px-4 py-2.5 rounded-xl text-sm font-semibold shadow-emerald transition-all">
            <Plus size={16} /> Tulis Berita
          </button>
        </div>
        <div className="p-4 md:p-8">
          <div className="space-y-3">
            {berita.map((b, i) => (
              <motion.div key={b.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
                className="bg-white rounded-2xl p-5 shadow-card border border-gray-50 flex items-start gap-4">
                {b.foto && <img src={b.foto} alt="" className="w-20 h-16 object-cover rounded-xl flex-shrink-0" />}
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-800 text-sm line-clamp-1">{b.judul}</h3>
                  <p className="text-xs text-gray-400 mt-0.5">{b.tanggal} · {b.penulis}</p>
                  <div className="flex items-center gap-2 mt-1.5">
                    <Eye size={12} className="text-gray-400" />
                    <span className="text-xs text-gray-400">{b.viewer_count || 0} pembaca</span>
                  </div>
                </div>
                <div className="flex gap-1.5 flex-shrink-0">
                  <button onClick={() => { setForm({ judul: b.judul, konten: b.konten, ringkasan: b.ringkasan||'', tanggal: b.tanggal, foto: b.foto||'' }); setEditId(b.id); setShowForm(true); }}
                    className="p-2 text-blue-500 hover:bg-blue-50 rounded-lg transition-colors"><Pencil size={14} /></button>
                  <button onClick={() => handleDelete(b.id)} className="p-2 text-red-400 hover:bg-red-50 rounded-lg transition-colors"><Trash2 size={14} /></button>
                </div>
              </motion.div>
            ))}
            {berita.length === 0 && <div className="text-center py-16 text-gray-400 text-sm">Belum ada berita. Tulis artikel pertama!</div>}
          </div>
        </div>

        <AnimatePresence>
          {showForm && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4"
              onClick={e => e.target === e.currentTarget && setShowForm(false)}>
              <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
                className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between p-6 border-b border-gray-100">
                  <h2 className="font-playfair text-xl font-bold text-emerald-950">{editId ? 'Edit Berita' : 'Tulis Berita'}</h2>
                  <button onClick={() => setShowForm(false)} className="p-2 hover:bg-gray-100 rounded-xl"><X size={18} /></button>
                </div>
                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Judul *</label>
                    <input required value={form.judul} onChange={e => setForm(f => ({...f, judul: e.target.value}))}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Ringkasan</label>
                    <input value={form.ringkasan} onChange={e => setForm(f => ({...f, ringkasan: e.target.value}))}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Konten *</label>
                    <textarea required value={form.konten} onChange={e => setForm(f => ({...f, konten: e.target.value}))} rows={6}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 resize-none" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Tanggal *</label>
                    <input required type="date" value={form.tanggal} onChange={e => setForm(f => ({...f, tanggal: e.target.value}))}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20" />
                  </div>
                  <ImageUpload value={form.foto} onChange={v => setForm(f => ({...f, foto: v}))} label="Foto Berita (opsional)" />
                  <div className="flex gap-3 pt-2">
                    <button type="button" onClick={() => setShowForm(false)}
                      className="flex-1 py-2.5 border border-gray-200 rounded-xl text-sm font-semibold text-gray-600">Batal</button>
                    <button data-testid="berita-form-submit" type="submit"
                      className="flex-1 py-2.5 bg-emerald-500 text-white rounded-xl text-sm font-semibold shadow-emerald flex items-center justify-center gap-2">
                      <Check size={15} /> Terbitkan
                    </button>
                  </div>
                </form>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
